import React from 'react';
import Table from '../common/components/Table';
import SectionHomeWrapper from '../common/wrappers/SectionHomeWrapper';
import PortOpeningRequestOnPrem from '../../models/entities/PortOpeningRequestOnPrem';
import OnPremPortOpeningRequestService from '../../services/OnPremPortOpeningRequestService';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import AlertModal from '../common/modals/AlertModal';
import Paginator from '../common/tables/Paginator';
import './OnPremPortOpeningRequestsHome.css';
import CheckboxFilter, { CheckboxOption } from '../common/tables/CheckboxFilter';
import Filter from '../common/tables/Filter';
import styled from 'styled-components';
import WorkflowStateBadge from '../common/WorkflowStateBadge';
import { Params, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { OnPremPortOpeningRequestsTypes } from '../../models/enums/POR/OnPremPortOpeningRequestsTypes';
import { PortOpeningRequestsDirection } from '../../models/enums/POR/PortOpeningRequestsDirection';
import { RuleAction } from '../../models/enums/POR/RuleAction';
import { RuleOperation } from '../../models/enums/POR/RuleOperation';
import { authenticatedUser } from '../../authentication/authModule';
import { UserRole } from '../../models/enums/UserRole';
import Memoized from '../common/Memoized';
import DateRangePicker from '../common/DateRangePicker';
import '../common/CommonElements.css';
import { Icon } from '../common/components/Icon';
import { Searchbar } from '../common/components/Searchbar';
import { AlertVariantType, Button, ProgressBar, Select, ToggleField } from '../common';
import { ButtonGroup } from '../common/components/ButtonGroup';
import { Toolbar, ToolbarItem } from '../common/components/Toolbar';
import { TextLink } from '../common/components/TextLinkNew';
import { Tab, TabItem } from '@emorg-prd/standard-react';
import PortOpeningRuleOnPrem from '../../models/entities/PortOpeningRuleOnPrem';
import PORTypeBadge from '../PortOpeningRequests/components/PORTypeBadge';
import RuleModalInternetDMZ from './components/porRule/RuleModalInternetDMZ';
import RuleModalCertifiedConnection from './components/porRule/RuleModalCertifiedConnection';

export type PORFilters = {
	type: CheckboxOption[];
	scope: CheckboxOption[];
	status: CheckboxOption[];
	searchUser: string;
	orderBy: string;
	fromDate: string;
	toDate: string;
};

export type RuleFilters = {
	operation: CheckboxOption[];
	action: CheckboxOption[];
	type: CheckboxOption[];
	searchRule: string;
	porType: string;
};

const MyP = styled.p`
	margin-right: 8px;
	margin-bottom: auto !important;
	margin-top: auto !important;
`;

const MyLink = styled.span`
	color: #0c69b0;
	text-decoration: underline;
	cursor: pointer;
`;

interface PortOpeningRequestsHomeProps extends PortOpeningRequestsHomeWithParamsProps {}

interface PortOpeningRequestsHomeWithParamsProps {
	type?: OnPremPortOpeningRequestsTypes[];
	subtitle?: string;
	navigate?: any;
	params?: Params<string>;
}

interface PortOpeningRequestsHomeWithParamsState {
	loading: boolean;
	requests: PortOpeningRequestOnPrem[];
	implementedRules: PortOpeningRuleOnPrem[];
	filteredRequests: PortOpeningRequestOnPrem[];
	filteredRules: PortOpeningRuleOnPrem[];
	endModal: AlertBanner;
	newConnectedPORInfoModalVisible: boolean;
	visibleRequests: PortOpeningRequestOnPrem[];
	visibleRules: PortOpeningRuleOnPrem[];
	selectedRule: PortOpeningRuleOnPrem | null;
	ruleModalVisible: boolean;
}

export type OnPremPORsMemoizedData = {
	searchUser: void;
	status: void;
	scope: void;
	type: void;
	orderBy: void;
	fromDate: void;
	toDate: void;
};

const defaultMemoizedDataStructure = {
	searchUser: undefined,
	orderBy: undefined,
	scope: undefined,
	type: undefined,
	status: undefined,
	fromDate: undefined,
	toDate: undefined,
} as OnPremPORsMemoizedData;

const memoized = new Memoized(defaultMemoizedDataStructure);

type OnPremRulesMemoizedData = {
	searchRule: void;
	operation: void;
	action: void;
	type: void;
	porType: void;
};

const defaultRuleMemoizedDataStructure = {
	searchRule: undefined,
	operation: undefined,
	action: undefined,
	type: undefined,
	porType: undefined,
} as OnPremRulesMemoizedData;

const ruleMemoized = new Memoized(defaultRuleMemoizedDataStructure);

// Caches the raw fetched onPremPors/implementedRules across mounts
const requestsCache: {
	onPremPors?: PortOpeningRequestOnPrem[];
	implementedRules?: PortOpeningRuleOnPrem[];
} = {
	onPremPors: undefined,
	implementedRules: undefined,
};

function refreshRequestsCache() {
	requestsCache.onPremPors = undefined;
	requestsCache.implementedRules = undefined;
}

const truncateItem = (s: string, maxChars = 25): string =>
	s.length > maxChars ? s.slice(0, maxChars) + '…' : s;

const truncateList = (value: string | undefined, max = 10): string => {
	if (!value) return '';
	const items = value.split(';').map((s) => s.trim()).filter(Boolean);
	const visible = items.slice(0, max).map((s) => truncateItem(s));
	if (items.length <= max) return visible.join('; ');
	return visible.join('; ') + ` ... (+${items.length - max} more)`;
};

class PortOpeningRequestsHomeWithParams extends React.Component<
	PortOpeningRequestsHomeWithParamsProps,
	PortOpeningRequestsHomeWithParamsState
> {
	OnPremPortOpeningRequestService: OnPremPortOpeningRequestService;

	filter: Filter<PORFilters>;
	ruleFilter: Filter<RuleFilters>;

	constructor(props) {
		super(props);
		this.OnPremPortOpeningRequestService = new OnPremPortOpeningRequestService();
		this.state = {
			loading: true,
			requests: [],
			implementedRules: [],
			filteredRequests: [],
			filteredRules: [],
			visibleRequests: [],
			visibleRules: [],
			selectedRule: null,
			ruleModalVisible: false,
			endModal: {
				visible: false,
			},
			newConnectedPORInfoModalVisible: false,
		};

		this.filter = new Filter(this.filterPORs, this.getFilters());
		this.ruleFilter = new Filter(this.filterRules, this.getRuleFilters());
	}

	async componentDidMount() {
		await this.loadRequests();
	}

	loadRequests = async (isRefresh: boolean = false) => {
		this.setState({ loading: true });
		if (isRefresh) refreshRequestsCache();
		const canSeeRules = authenticatedUser.isInRole(UserRole.POROnPremReader) ||
			authenticatedUser.isInRole(UserRole.PORToOnPremTechImplementer);

		try {
			const requestsPromise: Promise<PortOpeningRequestOnPrem[]> =
				typeof requestsCache.onPremPors !== 'undefined'
					? Promise.resolve(requestsCache.onPremPors)
					: this.OnPremPortOpeningRequestService.getAll();

			const rulesPromise: Promise<PortOpeningRuleOnPrem[]> | null = !canSeeRules
				? null
				: typeof requestsCache.implementedRules !== 'undefined'
					? Promise.resolve(requestsCache.implementedRules)
					: this.OnPremPortOpeningRequestService.getImplementedRules();

			const promises: [Promise<PortOpeningRequestOnPrem[]>, Promise<PortOpeningRuleOnPrem[]> | null] = [
				requestsPromise,
				rulesPromise,
			];

			const [requestsResult, rulesResult] = await Promise.allSettled(promises);

			if (requestsResult.status === 'fulfilled') {
				requestsCache.onPremPors = requestsResult.value;
				const requests = requestsResult.value.map((r) => new PortOpeningRequestOnPrem(r)).reverse();
				this.setState({ requests }, this.filter.filter);
			} else {
				this.handleEndModal('Failed to load requests, try again later.', 'error', 5000);
			}

			if (canSeeRules && rulesResult.status === 'fulfilled' && rulesResult.value) {
				requestsCache.implementedRules = rulesResult.value;
				const implementedRules = rulesResult.value.map((r) => new PortOpeningRuleOnPrem(r)).reverse();
				this.setState({ implementedRules }, this.ruleFilter.filter);
			} else if (canSeeRules && rulesResult.status === 'rejected') {
				console.error('Failed to load implemented rules:', (rulesResult as PromiseRejectedResult).reason);
			}
		} catch (error) {
			console.error('Unexpected error in loadRequests:', error);
			this.handleEndModal('Something went wrong, try again later.', 'error', 5000);
		} finally {
			this.setState({ loading: false });
		}
	};

	handleEndModal = (text: string, variant: AlertVariantType, timeout: number) => {
		this.setState(
			{
				endModal: {
					visible: true,
					text,
					variant,
				},
			},
			() => {
				setTimeout(() => {
					this.redirectToHome();
				}, timeout);
			}
		);
	};

	redirectToHome = () => {
		this.setState(
			{
				endModal: { visible: false },
			},
			() => {
				this.props.navigate('/');
			}
		);
	};

	getDefaultFilters = () => {
		const defaultFilters = {
			searchUser: '',
			status: [
				{ label: 'Submitted', value: 'Submitted', checked: true },
				{ label: 'Rejected', value: 'Rejected', checked: true },
				{ label: 'Draft', value: 'Draft', checked: true },
				{
					label: 'Pending Network Policy Solution Architecture approval',
					value: 'Pending Network Policy Solution Architecture approval',
					checked: true,
				},
				{
					label: 'Pending Operations Technology Cybersecurity Risk Management approval',
					value: 'Pending Operations Technology Cybersecurity Risk Management approval',
					checked: true,
				},
				{
					label: 'Pending Application Owner approval',
					value: 'Pending Application Owner approval',
					checked: true,
				},
				{ label: 'Pending for Policy Team Approval', value: 'Pending for Policy Team Approval', checked: true },
				{ label: 'Pending for Cyber Team Approval', value: 'Pending for Cyber Team Approval', checked: true },
				{
					label: 'Pending Network Connection Owner approval',
					value: 'Pending Network Connection Owner approval',
					checked: true,
				},
				{ label: 'Pending Proxy Architect approval', value: 'Pending Proxy Architect approval', checked: true },
				{ label: 'Pending Business DOAG Approval', value: 'Pending Business DOAG Approval', checked: true },
				{ label: 'Awaiting Implementation', value: 'Awaiting Implementation', checked: true },
				{ label: 'Review', value: 'Review', checked: true },
				{ label: 'Completed', value: 'Completed', checked: true },
				{ label: 'Cancelled', value: 'Cancelled', checked: false },
				{ label: 'Implementation Failed', value: 'Implementation Failed', checked: false },
				{ label: 'Error', value: 'Error', checked: false },
			],
			type: [
				{ label: 'L4 -> Internet', value: OnPremPortOpeningRequestsTypes.InternetDMZ.name, checked: true },
				{
					label: 'Internal -> Certified Connection',
					value: OnPremPortOpeningRequestsTypes.InternalCertConnection.name,
					checked: true,
				},
			],
			scope: [
				{
					label: PortOpeningRequestsDirection.L4ToInternet.displayName,
					value: PortOpeningRequestsDirection.L4ToInternet.name,
					checked: true,
				},
				{
					label: PortOpeningRequestsDirection.iBossProxyToInternet.displayName,
					value: PortOpeningRequestsDirection.iBossProxyToInternet.name,
					checked: true,
				},
				{
					label: PortOpeningRequestsDirection.InternetToNorthFirewall.displayName,
					value: PortOpeningRequestsDirection.InternetToNorthFirewall.name,
					checked: true,
				},
				{
					label: PortOpeningRequestsDirection.InternetToSouthFirewall.displayName,
					value: PortOpeningRequestsDirection.InternetToSouthFirewall.name,
					checked: true,
				},
				{
					label: PortOpeningRequestsDirection.L4ToL3LablanXTO.displayName,
					value: PortOpeningRequestsDirection.L4ToL3LablanXTO.name,
					checked: true,
				},
			],
			orderBy: 'newest',
			fromDate: undefined,
			toDate: undefined,
		} as PORFilters;

		return defaultFilters;
	};

	getFilters = () => {
		const defaultFilters = this.getDefaultFilters();

		const filters = {
			searchUser: memoized.getMemoizedData('searchUser', defaultFilters.searchUser),
			status: memoized.getMemoizedData('status', defaultFilters.status),
			type: memoized.getMemoizedData('type', defaultFilters.type),
			scope: memoized.getMemoizedData('scope', defaultFilters.scope),
			orderBy: memoized.getMemoizedData('orderBy', defaultFilters.orderBy),
			fromDate: memoized.getMemoizedData('fromDate', defaultFilters.fromDate),
			toDate: memoized.getMemoizedData('toDate', defaultFilters.toDate),
		} as PORFilters;

		return filters;
	};

	allFiltersChecked = (key: string) => {
		return this.getFilters()[key].reduce((acc, box) => acc && box.checked, true);
	};

	saveFilters = () => {
		memoized.saveMemoizeData('status', this.filter.filters.status);
		memoized.saveMemoizeData('searchUser', this.filter.filters.searchUser);
		memoized.saveMemoizeData('orderBy', this.filter.filters.orderBy);
		memoized.saveMemoizeData('scope', this.filter.filters.scope);
		memoized.saveMemoizeData('fromDate', this.filter.filters.fromDate);
		memoized.saveMemoizeData('toDate', this.filter.filters.toDate);
		memoized.saveMemoizeData('type', this.filter.filters.type);
	};

	getTraketOrAppID = (request: PortOpeningRuleOnPrem): string => {
		const por = this.state.requests.find(r => r.id === request.portOpeningRequestId);
		if (!por) return '-';
		const cartaAppId = por.cartaId?.length > 0 ? por.cartaId[0]?.cartaAppId : null;
		return cartaAppId ?? por.trackerID?.toString() ?? '-';
	};

	filterPORs = () => {
		let filteredRequests = [];

		this.saveFilters();

		const filterCallback = (por: PortOpeningRequestOnPrem) => {
			return (
				this.filter.filterCheckBox(
					'status',
					por.workflowInstance?.currentWorkflowInstanceState.onPremWorkflowState?.name
				) &&
				this.filter.filterCheckBox('type', por.type?.name) &&
				this.filter.filterCheckBox('scope', por.direction?.name) &&
				this.filter.isInDateRange('fromDate', 'toDate', new Date(por.workflowInstance.createdDate)) &&
				(this.filter.filterSearchText('searchUser', por.workflowInstance.createdBy.displayName) ||
					this.filter.filterSearchText('searchUser', por.contactUser?.displayName) ||
					this.filter.filterSearchText('searchUser', por.approvingUser?.displayName) ||
					this.filter.filterSearchText('searchUser', por.id.toString()))
			);
		};

		filteredRequests = this.state.requests.filter(filterCallback).sort((a, b) => {
			if (this.filter.filters.orderBy == undefined || this.filter.filters.orderBy === 'newest') {
				return a.id < b.id ? 1 : -1;
			}
			return a.id > b.id ? 1 : -1;
		});

		this.setState({
			filteredRequests: filteredRequests,
		});
	};

	goToNewRequest = () => {
		this.props.navigate('/onPremportOpeningRequests/new');
	};

	goToPORDetails = (request) => {
		this.props.navigate(`/onPremportOpeningRequests/${request.id}`);
	};

	clearSearchbar = (e) => {
		e.preventDefault();
		this.filter.updateFilter('', 'searchUser');
	};

	clearFilters = () => {
		memoized.resetAllMemoizeData();
		this.filter.updateFilters(this.getDefaultFilters());
	};

	getDefaultRuleFilters = () => {
		return {
			searchRule: '',
			porType: OnPremPortOpeningRequestsTypes.InternetDMZ.name,
			operation: [
				{ label: 'Add', value: RuleOperation.Add.name, checked: true },
				{ label: 'Remove', value: RuleOperation.Remove.name, checked: true },
				{ label: 'Modify', value: RuleOperation.Modify.name, checked: true },
				{ label: 'Keep', value: RuleOperation.Keep.name, checked: true },
			],
			action: [
				{ label: 'Allow', value: RuleAction.Allow.name, checked: true },
				{ label: 'Deny', value: RuleAction.Deny.name, checked: true },
				{ label: 'Block', value: RuleAction.Block.name, checked: true },
				{ label: 'Disable', value: RuleAction.Disable.name, checked: true },
				{ label: 'Remove', value: RuleAction.Remove.name, checked: true },
			],
			type: [
				{ label: 'Temporary', value: 'TEMP', checked: true },
				{ label: 'Permanent', value: 'PERM', checked: true },
			],
		} as RuleFilters;
	};

	getRuleFilters = () => {
		const defaultFilters = this.getDefaultRuleFilters();
		return {
			searchRule: ruleMemoized.getMemoizedData('searchRule', defaultFilters.searchRule),
			porType: ruleMemoized.getMemoizedData('porType', defaultFilters.porType),
			operation: ruleMemoized.getMemoizedData('operation', defaultFilters.operation),
			action: ruleMemoized.getMemoizedData('action', defaultFilters.action),
			type: ruleMemoized.getMemoizedData('type', defaultFilters.type),
		} as RuleFilters;
	};

	allRuleFiltersChecked = (key: string) => {
		return this.getRuleFilters()[key].reduce((acc: boolean, box: CheckboxOption) => acc && box.checked, true);
	};

	saveRuleFilters = () => {
		ruleMemoized.saveMemoizeData('searchRule', this.ruleFilter.filters.searchRule);
		ruleMemoized.saveMemoizeData('porType', this.ruleFilter.filters.porType);
		ruleMemoized.saveMemoizeData('operation', this.ruleFilter.filters.operation);
		ruleMemoized.saveMemoizeData('action', this.ruleFilter.filters.action);
		ruleMemoized.saveMemoizeData('type', this.ruleFilter.filters.type);
	};

	filterRules = () => {
		this.saveRuleFilters();

		const filterCallback = (rule: PortOpeningRuleOnPrem) => {
			return (
				this.ruleFilter.filterSearchText('porType', rule.type?.name) &&
				this.ruleFilter.filterCheckBox('operation', rule.operation?.name) &&
				this.ruleFilter.filterCheckBox('action', rule.action?.name) &&
				this.ruleFilter.filterCheckBox('type', rule.isTemporary ? 'TEMP' : 'PERM') &&
				(this.ruleFilter.filterSearchText('searchRule', rule.name) ||
					this.ruleFilter.filterSearchText('searchRule', rule.sourceIPs) ||
					this.ruleFilter.filterSearchText('searchRule', rule.destinationURLs) ||
					this.ruleFilter.filterSearchText('searchRule', rule.destinationIPs) ||
					this.ruleFilter.filterSearchText('searchRule', rule.description) ||
					this.ruleFilter.filterSearchText('searchRule', rule.id?.toString()) ||
					this.ruleFilter.filterSearchText('searchRule', this.getTraketOrAppID(rule)))
			);
		};

		this.setState((prevState) => {
			return { filteredRules: prevState.implementedRules.filter(filterCallback) };
		});
	};

	clearRuleFilters = () => {
		ruleMemoized.resetAllMemoizeData();
		this.ruleFilter.updateFilters(this.getDefaultRuleFilters());
	};

	handlePORTypeChange = (e: any) => {
		this.ruleFilter.handleSearchTextChange(e, 'porType');
	};

	render() {
		const rows = this.state.visibleRequests?.map((request) => (
			<Table.Row key={request.id} onClick={() => this.goToPORDetails(request)}>
				<>{request.id}</>
				<>{request.type?.displayName}</>
				<>{request.direction?.displayName}</>
				<>{request.workflowInstance?.createdBy?.displayName}</>
				<>{request.approvingUser?.displayName || request.appEndorser?.displayName}</>
				<>{moment(new Date(request.workflowInstance.createdDate)).format('YYYY-MM-DD')}</>
				<>
					<WorkflowStateBadge
						workflowState={request.workflowInstance?.currentWorkflowInstanceState?.onPremWorkflowState}
						fullWidth
					/>
				</>
			</Table.Row>
		));

		const ruleRows = this.state.visibleRules?.map((request) => (
			<Table.Row key={request.id} onClick={() =>	this.setState({ selectedRule: request, ruleModalVisible: true } )}>
				<>{request.portOpeningRequestId}</>
				<>{this.getTraketOrAppID(request)}</>
				<>{request.name}</>
				<>{request.isTemporary ? <PORTypeBadge >Temporary</PORTypeBadge> : <PORTypeBadge >Permanent</PORTypeBadge>}</>
				<>{request.action.name}</>
				<>{truncateList(request.sourceIPs)}</>
				<>{truncateList(request.destinationURLs)}</>
				<>{truncateList(request.destinationIPs)}</>
				<>{request.destinationPorts}</>
				<>{request.description}</>
			</Table.Row>
		));

		return (
			<>
				<SectionHomeWrapper
					title={`Port Opening Requests${this.props.subtitle ? `: ${this.props.subtitle}` : ''}`}
				>
					<p>
						For more information about PORs{' '}
						<TextLink className="em-u-margin-right-none" external target="_blank" href="https://goto/por">
							Click here
						</TextLink>
					</p>
					{authenticatedUser.isInRole(UserRole.POROnPremRequestor) ? (
						<></>
					) : (
						<p>
							For requesting PORs{' '}
							<TextLink
								className="em-u-margin-right-none"
								external
								target="_blank"
								href="https://goto/POR"
							>
								Click here
							</TextLink>
						</p>
					)}					
					{this.state.loading ? (
						<ProgressBar label="Loading requests..." className="em-u-margin-top-half" indeterminate />
					) : (
						<>
							<Tab type="em-c-tabs--underline" optionalClass='onPrem-tab-content'>
								<TabItem title="See by POR">
									<Table
										header={
											<>
												<Toolbar>
													<ToolbarItem width="lg" stretch>
														<Searchbar
															condensed={true}
															placeholder="Search all fields shown below (i.e. POR#, Requestor, etc.)"
															value={this.filter.filters.searchUser}
															onChange={(e) =>
																this.filter.handleSearchTextChange(e, 'searchUser')
															}
															onSubmit={(e) => this.clearSearchbar(e)}
														/>
													</ToolbarItem>
													<ToolbarItem right>
														<Button
															disabled={!authenticatedUser.isInRole(UserRole.POROnPremRequestor)}
															variant="primary"
															size="small"
															isLoading={this.state.loading}
															onClick={() => {
																this.goToNewRequest();
															}}
														>
															<Icon size="small" icon="plus-filled" />
															<span>New Request</span>
														</Button>
													</ToolbarItem>
												</Toolbar>
												<Toolbar>
													<ToolbarItem>
														<DateRangePicker
															toDateOnChangeFunction={(e) =>
																this.filter.handleSearchTextChange(e, 'toDate')
															}
															fromDateOnChangeFunction={(e) =>
																this.filter.handleSearchTextChange(e, 'fromDate')
															}
															fromDateValue={this.filter.filters.fromDate}
															toDateValue={this.filter.filters.toDate}
															containerStyle={{ position: 'relative', bottom: '8px' }}
															pickerStyle={{ margin: '0px' }}
														/>
													</ToolbarItem>
													<ToolbarItem>
														<CheckboxFilter
															label="Type"
															buttonProps={{ size: 'small' }}
															onChange={(event) =>
																this.filter.handleCheckboxChange(event, 'type')
															}
															options={this.filter.filters.type}
															allDefaultChecked={this.allFiltersChecked('type')}
														/>
													</ToolbarItem>
													<ToolbarItem>
														<CheckboxFilter
															label="Scope"
															buttonProps={{ size: 'small' }}
															onChange={(event) =>
																this.filter.handleCheckboxChange(event, 'scope')
															}
															options={this.filter.filters.scope}
															allDefaultChecked={this.allFiltersChecked('scope')}
														/>
													</ToolbarItem>
													<ToolbarItem>
														<CheckboxFilter
															label="Status"
															onChange={(event) =>
																this.filter.handleCheckboxChange(event, 'status')
															}
															options={this.filter.filters.status}
															allDefaultChecked={this.allFiltersChecked('status')}
														/>
													</ToolbarItem>
													<ToolbarItem>
														<MyLink onClick={() => this.clearFilters()}>Clear filters</MyLink>
													</ToolbarItem>
													<ToolbarItem right>
														<ButtonGroup>
															<MyP>Sort by</MyP>
															<Select
																value={this.filter.filters.orderBy}
																onChange={(e) => {
																	this.filter.handleSearchTextChange(e, 'orderBy');
																}}
																options={[
																	{ text: 'Newest', value: 'newest' },
																	{ text: 'Oldest', value: 'oldest' },
																]}
															/>
														</ButtonGroup>
													</ToolbarItem>
												</Toolbar>
											</>
										}
										footer={
											<Paginator
												data={this.state.filteredRequests}
												onPageChange={(page) => {
													this.setState({
														visibleRequests: page,
													});
												}}
											/>
										}
									>
										<Table.Head>
											<Table.Head.Row>
												<Table.Head.Cell> POR # </Table.Head.Cell>
												<Table.Head.Cell className="em-u-text-align-center"> Type </Table.Head.Cell>
												<Table.Head.Cell className="em-u-text-align-center"> Scope </Table.Head.Cell>
												<Table.Head.Cell className="em-u-text-align-center">
													{' '}
													Requestor{' '}
												</Table.Head.Cell>
												<Table.Head.Cell className="em-u-text-align-center"> Approver </Table.Head.Cell>
												<Table.Head.Cell className="em-u-text-align-center">
													{' '}
													Requested Date{' '}
												</Table.Head.Cell>
												<Table.Head.Cell className="em-u-text-align-center"> Status </Table.Head.Cell>
											</Table.Head.Row>
										</Table.Head>
										<Table.Body>{rows}</Table.Body>
									</Table>
								</TabItem>
								{(authenticatedUser.isInRole(UserRole.POROnPremReader) || authenticatedUser.isInRole(UserRole.PORToOnPremTechImplementer)) && (
								<TabItem title="See by Rule">
									<Table
										header={
											<>
												<Toolbar>
													<ToolbarItem width='lg' stretch>
														<Searchbar
															condensed={true}
															placeholder="Search all fields shown below (i.e. Traker/App ID, Name, etc.)"
															value={this.ruleFilter.filters.searchRule}
															width={'lg'}
															onChange={(e) =>
																this.ruleFilter.handleSearchTextChange(e, 'searchRule')
															}
															onSubmit={(e) => {
																e.preventDefault();
																this.ruleFilter.updateFilter('', 'searchRule');
															}}
														/>
													</ToolbarItem>
												</Toolbar>
												<Toolbar>
													<ToolbarItem>
														<ToggleField
																label=""
																className="em-u-margin-none"
																value={this.ruleFilter.filters.porType}
																options={[
																	{
																		value: OnPremPortOpeningRequestsTypes.InternetDMZ.name,
																		text: 'L4 -> Internet',
																	},
																	{
																		value: OnPremPortOpeningRequestsTypes.InternalCertConnection.name,
																		text: 'Internal -> Certified Connection',
																	},
																]}
																onChange={(e) => this.handlePORTypeChange(e)}
																id={'porTypeToggle'}														/>
													</ToolbarItem>
													<ToolbarItem>
														<CheckboxFilter
															label="Operation"
															buttonProps={{ size: 'small' }}
															onChange={(event) =>
																this.ruleFilter.handleCheckboxChange(event, 'operation')
															}
															options={this.ruleFilter.filters.operation}
															allDefaultChecked={this.allRuleFiltersChecked('operation')}
														/>
													</ToolbarItem>
													<ToolbarItem>
														<CheckboxFilter
															label="Action"
															buttonProps={{ size: 'small' }}
															onChange={(event) =>
																this.ruleFilter.handleCheckboxChange(event, 'action')
															}
															options={this.ruleFilter.filters.action}
															allDefaultChecked={this.allRuleFiltersChecked('action')}
														/>
													</ToolbarItem>
													<ToolbarItem>
														<CheckboxFilter
															label="Type"
															buttonProps={{ size: 'small' }}
															onChange={(event) =>
																this.ruleFilter.handleCheckboxChange(event, 'type')
															}
															options={this.ruleFilter.filters.type}
															allDefaultChecked={this.allRuleFiltersChecked('type')}
														/>
													</ToolbarItem>
													<ToolbarItem>
														<MyLink onClick={() => this.clearRuleFilters()}>Clear filters</MyLink>
													</ToolbarItem>
												</Toolbar>
											</>
										}
										footer={
											<Paginator
												data={this.state.filteredRules}
												onPageChange={(page) => {
													this.setState({
														visibleRules: page,
													});
												}}
											/>
										}
									>
										<Table.Head>
											<Table.Head.Row>
												<Table.Head.Cell>POR#</Table.Head.Cell>
												<Table.Head.Cell className="em-u-text-align-center">{this.ruleFilter.filters.porType == "InternetDMZ" ? "App ID" : "Traker ID"}</Table.Head.Cell>
												<Table.Head.Cell className="em-u-text-align-center">NAME</Table.Head.Cell>
												<Table.Head.Cell className="em-u-text-align-center">TYPE</Table.Head.Cell>
												<Table.Head.Cell className="em-u-text-align-center">Action</Table.Head.Cell>
												<Table.Head.Cell className="em-u-text-align-center">SOURCE IP</Table.Head.Cell>
												<Table.Head.Cell className="em-u-text-align-center">DEST. URLS</Table.Head.Cell>
												<Table.Head.Cell className="em-u-text-align-center">DEST. IPS</Table.Head.Cell>
												<Table.Head.Cell className="em-u-text-align-center">PORTS</Table.Head.Cell>
												<Table.Head.Cell className="em-u-text-align-center">DESCRIPTION</Table.Head.Cell>
											</Table.Head.Row>
										</Table.Head>
										<Table.Body>{ruleRows}</Table.Body>
									</Table>
								</TabItem>
								)}
							</Tab>
						</>
					)}
				</SectionHomeWrapper>
				<AlertModal {...this.state.endModal} willTimeout={false} onClose={this.redirectToHome} />
				{this.state.selectedRule?.type?.name === OnPremPortOpeningRequestsTypes.InternetDMZ.name && (
					<RuleModalInternetDMZ
						visible={this.state.ruleModalVisible}
						isReadOnly
						editingRule={this.state.selectedRule}
						direction={this.state.selectedRule?.direction}
						isFromHistory = {true}
						onClose={() => this.setState({ ruleModalVisible: false, selectedRule: null })}
						onConfirm={() => this.setState({ ruleModalVisible: false, selectedRule: null })}
					/>
				)}
				{this.state.selectedRule?.type?.name === OnPremPortOpeningRequestsTypes.InternalCertConnection.name && (
					<RuleModalCertifiedConnection
						visible={this.state.ruleModalVisible}
						isReadOnly
						editingRule={this.state.selectedRule}
						isFromHistory = {true}
						onClose={() => this.setState({ ruleModalVisible: false, selectedRule: null })}
						onConfirm={() => this.setState({ ruleModalVisible: false, selectedRule: null })}
					/>
				)}
			</>
		);
	}
}

const OnPremPortOpeningRequestsHome = (props: PortOpeningRequestsHomeProps) => {
	return (
		<PortOpeningRequestsHomeWithParams
			type={props.type}
			subtitle={props.subtitle}
			params={useParams()}
			navigate={useNavigate()}
		/>
	);
};

export default OnPremPortOpeningRequestsHome;
